import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFavorites } from '../contexts/FavoritesContext';
import { useToast } from '../contexts/ToastContext';
import './Favorites.css';
import { proxyBucketUrl, handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageUtils';

const Favorites: React.FC = () => {
  const navigate = useNavigate();
  const { state, toggleFavorite, loadFavorites } = useFavorites();
  const { showToast } = useToast();
  const favorites = state.items;

  const handleRemoveFromFavorites = async (productId: number) => {
    try {
      await toggleFavorite(productId);
    } catch (err: any) {
      console.error('Failed to remove from favorites:', err);
      showToast('Failed to remove from favorites. Please try again.', 'error');
    }
  };

  // Loading state
  if (state.loading) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-loading">
            <div className="loading-spinner"></div>
            <p>Loading your favorites...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-error">
            <h3>Error Loading Favorites</h3>
            <p>{state.error}</p>
            <button onClick={loadFavorites} className="retry-btn">
              Try Again
            </button>
            <Link to="/shop" className="continue-shopping-btn">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Render favorites (both empty and filled states)
  return (
    <div className="favorites-page">
      <div className="favorites-container">
        <div className={`favorites-header ${favorites.length > 0 ? 'has-items' : ''}`}>
          <div className="empty-heart-icon">☆</div>
          <h1>Wishlist</h1>
          <p className="favorites-subtitle">{favorites.length === 0 ? 'Start exploring our unique vintage collection and add pieces you love!' : `Your curated collection of vintage treasures (${favorites.length} ${favorites.length === 1 ? 'item' : 'items'})`}</p>
          {favorites.length === 0 && (
            <>
              <div style={{marginTop: '2rem'}}></div>
              <Link to="/shop" className="start-shopping-btn">
                Discover Vintage Treasures
              </Link>
            </>
          )}
        </div>

        {favorites.length > 0 && (
          <div className="favorites-content">
            <div className="favorites-grid">
              {favorites.map(product => (
                <div key={product.id} className="favorite-item" onClick={() => navigate(`/product/${product.id}`)}>
                  <div className="product-card-inner">
                    <div className="product-image-wrapper">
                      <button
                        className="remove-favorite-btn active"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFromFavorites(product.id);
                        }}
                        title="Remove from favorites"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#ffffff" strokeWidth="1.5"/>
                        </svg>
                      </button>

                      <div className="product-images">
                        <img
                          src={proxyBucketUrl(product.images?.[0]) || PLACEHOLDER_IMAGE}
                          alt={product.name}
                          className="primary-image"
                          onError={handleImageError}
                        />
                      </div>
                    </div>

                    <div className="product-info">
                      <Link to={`/product/${product.id}`} className="product-name-link">
                        <h3 className="product-name">{product.name}</h3>
                      </Link>

                      <div className="product-footer">
                        <div className="price-section">
                          <span className="current-price">€{Number(product.price).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="favorites-actions">
              <Link to="/shop" className="continue-shopping-btn">Continue Shopping</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
